/**
 * PrelaunchGate — wraps the entire app and decides whether to show the
 * pre-launch portal or the real site.
 *
 * When `prelaunch_portal_enabled` is true:
 *   • Public routes ("/", "/shop", "/peptides", "/checkout", etc.) → portal
 *   • Admin routes ("/admin*", "/admin-login") → real site (admins still log in)
 *   • Authenticated customers ("/dashboard*") → real site (so existing accounts can still log in)
 *   • API/uploads/asset paths → never gated (handled by ingress, not this gate)
 *
 * When disabled: renders children unchanged. The real site comes back instantly.
 */
import { Suspense, lazy } from "react";
import { useLocation } from "react-router-dom";
import { usePrelaunchConfig } from "@/hooks/usePrelaunchConfig";

const PrelaunchPortalPage = lazy(() => import("@/pages/PrelaunchPortalPage"));

// Paths that ALWAYS bypass the pre-launch gate so existing admins, logged-in
// users, and email-link recipients can keep operating the real site.
const BYPASS_PREFIXES = [
  "/admin",         // admin panel + admin login
  "/dashboard",     // existing customer dashboards
  "/login",         // customer login (email/password + Google)
  "/auth-callback", // Google OAuth return URL — must complete session exchange
  "/_internal",     // future system pages
];

function shouldBypass(pathname) {
  return BYPASS_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p));
}

export default function PrelaunchGate({ children }) {
  // Site officially launched (Jun 2026): the gate is permanently retired so the
  // real store always renders — no config fetch, no boot spinner, no env risk.
  // To resurrect the portal, restore the usePrelaunchConfig() gating logic below.
  return children;
}

// eslint-disable-next-line no-unused-vars
function LegacyPrelaunchGate({ children }) {
  const { loading, enabled, config } = usePrelaunchConfig();
  const { pathname } = useLocation();

  // While we don't know yet, render NOTHING to avoid a flash of the real site
  // before the portal can intercept. The check is sub-200ms in normal conditions.
  if (loading) {
    return (
      <div className="min-h-screen bg-[#06070A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00AEEF]/30 border-t-[#00AEEF] rounded-full animate-spin" />
      </div>
    );
  }

  if (enabled && !shouldBypass(pathname)) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#06070A]" />}>
        <PrelaunchPortalPage config={config} />
      </Suspense>
    );
  }

  return children;
}
